import React from 'react';
import { Container, ColumnLayout, Header, SpaceBetween } from '@amzn/awsui-components-core';
import MetadataSelectInput from '../MetadataSelectInput';
import MetadataMultiselectInputV3 from '../MetadataMultiselectInputV3';
import { TechDomain } from '@amzn/awscat-pattern-library-service-model';
import {
  TechDomainOptions,
  AWSServiceOptions,
  WorkloadOptions,
  RTypeOptions,
  resolveWorkloadSelection,
} from '../../../utils/SelectableOptions';
import constants from '../../../../common/constants';

interface Step2ContentMetadataProps {
  readonly mainTechDomain: string | undefined;
  readonly setMainTechDomain: (value: string | undefined) => void;
  readonly techDomains: string[];
  readonly setTechDomains: (value: string[]) => void;
  readonly awsServices: string[];
  readonly setAwsServices: (value: string[]) => void;
  readonly workload: string[];
  readonly setWorkload: (value: string[]) => void;
  readonly rType: string | undefined;
  readonly setRType: (value: string | undefined) => void;
  readonly showValidationError?: boolean;
}

const Step2ContentMetadata: React.FC<Step2ContentMetadataProps> = ({
  mainTechDomain,
  setMainTechDomain,
  techDomains,
  setTechDomains,
  awsServices,
  setAwsServices,
  workload,
  setWorkload,
  rType,
  setRType,
  showValidationError = false,
}) => {
  const handleMainTechDomainChange = (_sectionId: string, value: string) => {
    setMainTechDomain(value || undefined);
    // Clear rType when Migration is not selected in either main or additional technologies
    if (value !== TechDomain.Migration && !techDomains.includes(TechDomain.Migration)) {
      setRType(undefined);
    }
  };

  const handleTechDomainsChange = (_sectionId: string, values: string[]) => {
    setTechDomains(values);
    // Clear rType when Migration is not selected in either main or additional technologies
    if (mainTechDomain !== TechDomain.Migration && !values.includes(TechDomain.Migration)) {
      setRType(undefined);
    }
  };

  const handleAwsServicesChange = (_sectionId: string, values: string[]) => {
    setAwsServices(values);
  };

  const handleWorkloadChange = (_sectionId: string, values: string[]) => {
    setWorkload(resolveWorkloadSelection(values, workload));
  };

  const handleRTypeChange = (_sectionId: string, value: string) => {
    setRType(value || undefined);
  };

  const isMigrationSelected =
    mainTechDomain === TechDomain.Migration || techDomains.includes(TechDomain.Migration);

  // Filter out the main tech domain from additional technologies options
  const filteredTechDomainOptions = TechDomainOptions.filter(
    (option) => option.value !== mainTechDomain,
  );

  const selectedTechDomainOptions = filteredTechDomainOptions.filter((option) =>
    techDomains.includes(option.value ?? ''),
  );

  const selectedAwsServiceOptions = AWSServiceOptions.filter((option) =>
    awsServices.includes(option.value ?? ''),
  );

  const selectedWorkloadOptions = WorkloadOptions.filter((option) =>
    workload.includes(option.value ?? ''),
  );

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header variant="h3">{constants.CREATE_CONTENT_WIZARD_STEP2_TECH_DOMAINS_HEADER}</Header>
        }
      >
        <ColumnLayout columns={2}>
          <MetadataSelectInput
            sectionId="mainTechDomain"
            label={constants.CREATE_CONTENT_WIZARD_STEP2_MAIN_TECH_LABEL}
            description={constants.CREATE_CONTENT_WIZARD_STEP2_MAIN_TECH_DESCRIPTION}
            contextualHelp="technologies"
            selectedId={mainTechDomain ?? ''}
            selectOptions={TechDomainOptions}
            onSelectUpdated={handleMainTechDomainChange}
            placeholder={constants.CREATE_CONTENT_WIZARD_STEP2_MAIN_TECH_PLACEHOLDER}
            isRequired
            visible
            showValidationError={showValidationError}
          />
          <MetadataMultiselectInputV3
            sectionId="techDomains"
            label={constants.CREATE_CONTENT_WIZARD_STEP2_ADDITIONAL_TECH_LABEL}
            description={constants.CREATE_CONTENT_WIZARD_STEP2_ADDITIONAL_TECH_DESCRIPTION}
            contextualHelp="technologies"
            selectedOptions={selectedTechDomainOptions}
            selectOptions={filteredTechDomainOptions}
            onSelectsUpdated={handleTechDomainsChange}
            placeholder={constants.CREATE_CONTENT_WIZARD_STEP2_ADDITIONAL_TECH_PLACEHOLDER}
            isRequired={false}
          />
        </ColumnLayout>
      </Container>

      <Container
        header={
          <Header variant="h3">
            {constants.CREATE_CONTENT_WIZARD_STEP2_OTHER_METADATA_HEADER}
          </Header>
        }
      >
        <ColumnLayout columns={2}>
          <MetadataMultiselectInputV3
            sectionId="awsServices"
            label={constants.CREATE_CONTENT_WIZARD_STEP2_AWS_SERVICES_LABEL}
            description={constants.CREATE_CONTENT_WIZARD_STEP2_AWS_SERVICES_DESCRIPTION}
            selectedOptions={selectedAwsServiceOptions}
            selectOptions={AWSServiceOptions}
            onSelectsUpdated={handleAwsServicesChange}
            placeholder={constants.CREATE_CONTENT_WIZARD_STEP2_AWS_SERVICES_PLACEHOLDER}
            isRequired={false}
          />
          <MetadataMultiselectInputV3
            sectionId="workload"
            label={constants.CREATE_CONTENT_WIZARD_STEP2_WORKLOAD_LABEL}
            description={constants.CREATE_CONTENT_WIZARD_STEP2_WORKLOAD_DESCRIPTION}
            contextualHelp="workload"
            selectedOptions={selectedWorkloadOptions}
            selectOptions={WorkloadOptions}
            onSelectsUpdated={handleWorkloadChange}
            placeholder={constants.CREATE_CONTENT_WIZARD_STEP2_WORKLOAD_PLACEHOLDER}
            isRequired={false}
          />
          {isMigrationSelected && (
            <MetadataSelectInput
              sectionId="rType"
              label={constants.RTYPE_LABEL}
              description="Choose a migration strategy"
              contextualHelp="rtype"
              selectedId={rType ?? ''}
              selectOptions={RTypeOptions}
              onSelectUpdated={handleRTypeChange}
              placeholder={constants.RTYPE_PLACEHOLDER}
              visible
              isRequired={true}
            />
          )}
        </ColumnLayout>
      </Container>
    </SpaceBetween>
  );
};

export default Step2ContentMetadata;
